import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

/**
 * Admin · Financial Rules
 *
 * Two global dials the whole platform reads on every price computation:
 *   1. Per-material GST rates (Cement, TMT Steel, Other).
 *   2. A single master switch that turns off *every* tier discount
 *      (super stockist / wholesaler / dealer / booking agent) with one
 *      click.
 *
 * All changes take effect immediately and are namespaced under
 * ``settings.financial_rules`` in Mongo.
 */
export default function AdminFinancialRules() {
  const { api } = useAuth();
  const [rules, setRules] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/financial-rules");
      setRules(r.data);
    } catch { toast.error("Could not load financial rules"); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  if (!rules) return <div className="font-body text-sm text-[#8A8A82]">Loading…</div>;

  const pct = (n) => `${Math.round((Number(n) || 0) * 100)}%`;
  const setGst = (k, val) => setRules(r => ({ ...r, gst: { ...r.gst, [k]: Number(val) / 100 } }));
  const setTier = (k, val) => setRules(r => ({ ...r, tier_discounts: { ...r.tier_discounts, [k]: Number(val) / 100 } }));

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put("/admin/financial-rules", {
        gst: rules.gst,
        bulk_tier_discounts_enabled: rules.bulk_tier_discounts_enabled,
        tier_discounts: rules.tier_discounts,
      });
      setRules(r.data);
      toast.success("Financial rules saved · live for the next quote");
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  const toggleTier = () => {
    setRules(r => ({ ...r, bulk_tier_discounts_enabled: !r.bulk_tier_discounts_enabled }));
  };

  return (
    <div className="space-y-6" data-testid="admin-financial-rules">
      <div className="border-l-2 border-[#B87333] pl-5">
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#B87333]">Vinci · Financial Rules</div>
        <h2 className="mt-1 font-serif text-3xl text-[#F5F5F0]">GST &amp; Bulk-tier controls</h2>
        <p className="mt-2 font-body text-sm text-[#8A8A82] max-w-2xl">
          Two dials that reshape every price the platform computes. Changes take effect on the next quote &mdash; no deploy required.
        </p>
      </div>

      <section className="bg-[#0B0B0B] border border-white/10 rounded-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#B87333]">GST Rates by Material</div>
            <div className="font-serif text-xl text-[#F5F5F0] mt-1">Global tax rates</div>
            <div className="font-body text-[11px] text-[#8A8A82] mt-1">
              Used by the Bulk Price Uploader, the Store checkout, and every invoice PDF. Cement defaults to 28%, TMT / other 18%.
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[["cement", "Cement"], ["tmt", "TMT Steel"], ["other", "Other Materials"]].map(([k, label]) => (
            <label key={k} className="block">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] mb-1.5">{label}</div>
              <div className="relative">
                <input type="number" step="0.5" min="0" max="100"
                  value={Math.round((rules.gst?.[k] || 0) * 100)}
                  onChange={(e) => setGst(k, e.target.value)}
                  className="w-full bg-transparent border border-white/15 focus:border-[#D8B23E] outline-none px-3 py-2 pr-10 font-mono text-lg text-[#F5F5F0] rounded-sm"
                  data-testid={`fin-gst-${k}`} />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 font-mono text-sm text-[#8A8A82]">%</span>
              </div>
            </label>
          ))}
        </div>
      </section>

      <section className="bg-[#0B0B0B] border border-white/10 rounded-sm p-6">
        <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#B87333]">Bulk-Tier Discounts</div>
            <div className="font-serif text-xl text-[#F5F5F0] mt-1">One-click master switch</div>
            <div className="font-body text-[11px] text-[#8A8A82] mt-1">
              When OFF, every category (super stockist / wholesaler / dealer / booking agent) sees the flat rate. Flip back ON to restore tiered discounts across the platform.
            </div>
          </div>
          <button type="button" onClick={toggleTier}
            className={`relative w-24 h-9 rounded-full transition-colors ${rules.bulk_tier_discounts_enabled ? "bg-[#2FB999]" : "bg-white/10 border border-white/15"}`}
            data-testid="fin-tier-toggle">
            <span className={`absolute top-1 h-7 w-7 rounded-full bg-[#F5F5F0] transition-all ${rules.bulk_tier_discounts_enabled ? "left-[62px]" : "left-1"}`} />
            <span className={`absolute inset-0 flex items-center ${rules.bulk_tier_discounts_enabled ? "justify-start pl-3" : "justify-end pr-3"} text-[9px] uppercase tracking-[0.22em] ${rules.bulk_tier_discounts_enabled ? "text-[#0A0A0A]" : "text-[#8A8A82]"}`}>
              {rules.bulk_tier_discounts_enabled ? "On" : "Off"}
            </span>
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[["super_stockist","Super Stockist"],["wholesaler","Wholesaler"],["dealer","Dealer"],["booking_agent","Booking Agent"]].map(([k, label]) => (
            <label key={k} className={`block ${!rules.bulk_tier_discounts_enabled ? "opacity-40" : ""}`}>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] mb-1.5">{label}</div>
              <div className="relative">
                <input type="number" step="0.5" min="0" max="100"
                  disabled={!rules.bulk_tier_discounts_enabled}
                  value={Math.round((rules.tier_discounts?.[k] || 0) * 100)}
                  onChange={(e) => setTier(k, e.target.value)}
                  className="w-full bg-transparent border border-white/15 focus:border-[#D8B23E] outline-none px-3 py-2 pr-10 font-mono text-base text-[#F5F5F0] rounded-sm disabled:cursor-not-allowed"
                  data-testid={`fin-tier-${k}`} />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 font-mono text-xs text-[#8A8A82]">%</span>
              </div>
            </label>
          ))}
        </div>
      </section>

      <div className="flex items-center justify-between gap-4">
        <div className="font-body text-[11px] text-[#5A5A55]">
          {rules.updated_at && (
            <>Last saved {new Date(rules.updated_at).toLocaleString()}
            {rules.updated_by ? <> by <span className="text-[#8A8A82]">{rules.updated_by}</span></> : null}</>
          )}
        </div>
        <button type="button" onClick={save} disabled={busy}
          className="px-6 py-2 bg-[#D8B23E] text-[#0A0A0A] font-body text-[11px] uppercase tracking-[0.24em] rounded-sm hover:bg-[#c8a336] disabled:opacity-40"
          data-testid="fin-save">
          {busy ? "Saving…" : "Save Financial Rules"}
        </button>
      </div>

      <div className="border border-white/5 rounded-sm p-4 bg-black/30 font-mono text-[10px] text-[#5A5A55]" data-testid="fin-preview">
        cement: {pct(rules.gst?.cement)} · tmt: {pct(rules.gst?.tmt)} · other: {pct(rules.gst?.other)} · tier-discounts: <span className={rules.bulk_tier_discounts_enabled ? "text-[#2FB999]" : "text-[#E5484D]"}>{rules.bulk_tier_discounts_enabled ? "enabled" : "disabled"}</span>
      </div>
    </div>
  );
}
